import { differenceInMinutes, format, parse } from 'date-fns';
import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { useActiveOrganization } from '@/features/organization/api/get-active-organization';
import { zonedEvent, zonedWallTimeToUtc } from '@/lib/timezone';

import { useUpdateAppointment } from '../api/update-appointment';
import { appointmentEditForm } from '../edit';

export interface AppointmentRescheduleDialogProps {
  appointmentId: string;
  /** Appointment start as an ISO string (real UTC instant). */
  startDate: string;
  /** Appointment end as an ISO string (real UTC instant). */
  endDate: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after the new time is saved. */
  onDone?: () => void;
}

/**
 * Move an appointment to a new date and start time, keeping its duration. The
 * picked wall-clock is read in the business timezone, so the saved instant is
 * the same whatever zone the device is in.
 *
 * Used by the full booking page. It mirrors the Quick actions reschedule
 * dialog; folding that one onto this is still to do (#1410).
 */
export function AppointmentRescheduleDialog({
  appointmentId,
  startDate,
  endDate,
  open,
  onOpenChange,
  onDone,
}: AppointmentRescheduleDialogProps) {
  const { data: organization } = useActiveOrganization();
  const timeZone = organization?.timezone ?? 'UTC';

  const start = zonedEvent(startDate, timeZone);
  const end = zonedEvent(endDate, timeZone);
  const durationMinutes = Math.max(differenceInMinutes(end, start), 0);

  const [date, setDate] = useState(() => format(start, 'yyyy-MM-dd'));
  const [time, setTime] = useState(() => format(start, 'HH:mm'));

  // Re-seed from the current start each time the dialog opens.
  const seedDate = format(start, 'yyyy-MM-dd');
  const seedTime = format(start, 'HH:mm');
  useEffect(() => {
    if (!open) return;
    setDate(seedDate);
    setTime(seedTime);
  }, [open, seedDate, seedTime]);

  const { updateAppointment, isUpdating } = useUpdateAppointment({
    onSuccess: () => {
      onOpenChange(false);
      onDone?.();
    },
  });

  const save = () => {
    const [year, month, day] = date.split('-').map(Number);
    const [hours, minutes] = time.split(':').map(Number);
    if (!year || !month || !day || Number.isNaN(hours) || Number.isNaN(minutes))
      return;
    const newStart = zonedWallTimeToUtc(
      new Date(year, month - 1, day),
      hours,
      minutes,
      timeZone
    );
    const newEnd = new Date(newStart.getTime() + durationMinutes * 60 * 1000);
    updateAppointment({
      id: appointmentId,
      startDate: newStart.toISOString(),
      endDate: newEnd.toISOString(),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reschedule appointment</DialogTitle>
          <DialogDescription>
            Pick a new date and time. The duration ({durationMinutes} min) is
            preserved.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <Field>
            <FieldLabel htmlFor="reschedule-date">
              {appointmentEditForm.labels.date}
            </FieldLabel>
            <DatePicker
              id="reschedule-date"
              placeholder="Select date"
              value={date ? parse(date, 'yyyy-MM-dd', new Date()) : undefined}
              onChange={(d) => setDate(d ? format(d, 'yyyy-MM-dd') : '')}
            />
          </Field>
          <Field>
            <FieldLabel htmlFor="reschedule-time">
              {appointmentEditForm.labels.startTime}
            </FieldLabel>
            <Input
              id="reschedule-time"
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
            />
          </Field>
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isUpdating}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={save}
            disabled={isUpdating || !date || !time}
          >
            {isUpdating ? 'Saving...' : 'Reschedule'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
